import { useCallback, useMemo, useState } from "react";

import { LogUnionRowItem, RowType } from "@common/components/MasterTable/models/MasterTableModel";
import { useActivityTable } from "@common/components/ShakeTable/modules/useActivityTable";
import { Deps, useShakePagination } from "@common/hooks/useShakePaginaton";
import {
  fillActivityHistoryWithNetworkRequestType,
  generateActivityHistoryUnion,
  LogType,
  LogUnionType,
  NetworkRequest,
} from "@common/models/eventTracking";
import { ActivityHistoryFilter } from "@main/pages/shared/consumers/useActivityHistoryFilter";

import { useCrashesDeps } from "../../Crashes/Crashes";
import { useUserFeedbackDeps } from "../../UserFeedback/UserFeedback";

interface Props {
  teamId?: string;
  appId?: string;
  ticketKey?: string;
  type: TicketType;
  hasLogs?: boolean;
  filter: ActivityHistoryFilter;
  isWebApp: boolean | undefined;
}

type TicketType = "crash" | "user_feedback";

interface LogMap {
  idToLogMap: Map<string, LogUnionType>;
  idToIndexMap: Map<string, number>;
  indexToIdMap: Map<number, string>;
}

interface PaginationDeps {
  teamId: string;
  appId: string;
  ticketKey: string;
  type: TicketType;
  hasLogs: boolean;
  isWebApp: boolean;
}

export function useActivityHistoryApiConsumer({ teamId, appId, ticketKey, type, hasLogs, filter, isWebApp }: Props) {
  const { userFeedbackService } = useUserFeedbackDeps();
  const { crashesService } = useCrashesDeps();
  const [detailLog, setDetailLog] = useState<LogUnionType>();

  const pagination = useShakePagination<LogUnionType, LogUnionRowItem>({
    deps: [teamId, appId, ticketKey, type, hasLogs, isWebApp],
    fetchFn: ({ signal, deps }) => {
      const { teamId, appId, ticketKey, type, isWebApp } = parseDeps(deps);
      switch (type) {
        case "crash":
          return crashesService
            .getActivityHistory(teamId, appId, ticketKey, signal)
            .then((res) => fillActivityHistoryWithNetworkRequestType(res.data, isWebApp))
            .then((log) => generateActivityHistoryUnion(log).reverse())
            .then((logs) => logs.map(mapLogToTableRowItem))
            .then((data) => {
              return { total: 0, items: data };
            });

        case "user_feedback":
          return userFeedbackService
            .getActivityHistory(teamId, appId, ticketKey, signal)
            .then((res) => fillActivityHistoryWithNetworkRequestType(res.data, isWebApp))
            .then((log) => generateActivityHistoryUnion(log).reverse())
            .then((logs) => logs.map(mapLogToTableRowItem))
            .then((data) => {
              return { total: 0, items: data };
            });
      }
    },
    enabled: (deps) => {
      const { teamId, appId, ticketKey, type, hasLogs, isWebApp } = parseDeps(deps);
      return Boolean(teamId && appId && ticketKey && type && hasLogs) && isWebApp !== undefined;
    },
    queryKey: `activityHistory ${appId}`,
  });

  const finalItems = useMemo(() => {
    return filterActivityHistoryItems(filter, pagination?.data as LogUnionRowItem[]);
  }, [filter, pagination.data]);

  const activityTableProps = useActivityTable({
    // eslint-disable-next-line
    data: (finalItems as any) ?? [],
    onRowClick: (item) => setDetailLog(getLogWithID(item.id)),
  });

  const logMap = useMemo<LogMap>(() => {
    if (!finalItems) {
      return {
        idToLogMap: new Map(),
        idToIndexMap: new Map(),
        indexToIdMap: new Map(),
      };
    }
    return {
      idToLogMap: new Map(finalItems.map((log) => [log?.id, log])),
      idToIndexMap: new Map(finalItems.map((log, index) => [log?.id, index])),
      indexToIdMap: new Map(finalItems.map((log, index) => [index, log?.id])),
    };
  }, [finalItems]);

  const getLogWithID = useCallback(
    (logId: string) => {
      return logMap.idToLogMap.get(logId);
    },
    [logMap],
  );

  const getNextLog = useCallback(
    (currentLogId: string) => {
      const currentLogIndex = logMap.idToIndexMap.get(currentLogId);

      const nextLogIndex =
        currentLogIndex !== undefined && logMap.idToIndexMap.size > currentLogIndex && currentLogIndex + 1;

      const nextLogId = nextLogIndex && logMap.indexToIdMap.get(nextLogIndex);

      return nextLogId && logMap.idToLogMap.get(nextLogId);
    },
    [logMap],
  );

  const getPreviousLog = useCallback(
    (currentLogId: string) => {
      const currentLogIndex = logMap.idToIndexMap.get(currentLogId);

      const previousLogIndex = currentLogIndex !== undefined && logMap.idToIndexMap.size - 1 > 0 && currentLogIndex - 1;

      const previousLogId =
        previousLogIndex !== undefined && previousLogIndex !== false && logMap.indexToIdMap.get(previousLogIndex);

      return previousLogId && logMap.idToLogMap.get(previousLogId);
    },
    [logMap],
  );

  return {
    getLogWithID,
    getNextLog,
    getPreviousLog,
    items: finalItems,
    loading: pagination.isLoading,
    loadNext: pagination.loadNext,
    hasMore: pagination.hasMore,
    activityTableProps,
    detailLog,
    setDetailLog,
    error: pagination.isError,
  };
}

const parseDeps = (deps: Deps): PaginationDeps => {
  return {
    teamId: deps[0] as string,
    appId: deps[1] as string,
    ticketKey: deps[2] as string,
    type: deps[3] as TicketType,
    hasLogs: deps[4] as boolean,
    isWebApp: deps[5] as boolean,
  };
};

const mapLogToTableRowItem = (log: LogUnionType): LogUnionRowItem => {
  return {
    ...log,
    timestamp: log.timestamp,
    row_type: RowType.LOG_UNION,
  };
};

const filterActivityHistoryItems = (filter: ActivityHistoryFilter, items?: LogUnionRowItem[]) => {
  const searchText = filter.searchText.toLowerCase();

  return items?.filter((item: LogUnionRowItem | undefined) => {
    if (!item) return false;

    switch (item.instance_type) {
      case LogType.NETWORK_REQUEST:
        if (item.url?.toLowerCase()?.includes(searchText) && filterNetworkRequestByType(filter, item)) {
          const success = item.status_code?.startsWith("2") || item.status_code?.startsWith("3");
          return !filter.errorsOnly || (filter.errorsOnly && !success);
        }
        break;

      case LogType.CONSOLE_LOG:
        return filter.consoleLogs && item.message?.toLowerCase()?.includes(searchText);

      case LogType.CUSTOM_LOG:
        return filter.customLogs && item.message?.toLowerCase()?.includes(searchText);

      case LogType.NOTIFICATION:
        return filter.notificationEvents && item.description?.toLowerCase()?.includes(searchText);

      case LogType.SCREEN_EVENT:
        return filter.screenViews && item.description?.toLowerCase()?.includes(searchText);

      case LogType.USER_EVENT:
        return filter.userActions && item.description?.toLowerCase()?.includes(searchText);

      case LogType.SYSTEM_EVENT:
        return filter.systemEvents && item.description?.toLowerCase()?.includes(searchText);
    }

    return false;
  });
};

const filterNetworkRequestByType = (filter: ActivityHistoryFilter, item: NetworkRequest) => {
  const type = item.request_type;

  if (filter.networkRequestTypes.all) return true;
  if (type === "fetch" && filter.networkRequestTypes.fetch) return true;
  if (type === "document" && filter.networkRequestTypes.document) return true;
  if (type === "stylesheet" && filter.networkRequestTypes.stylesheet) return true;
  if (type === "script" && filter.networkRequestTypes.script) return true;
  if (type === "font" && filter.networkRequestTypes.font) return true;
  if (type === "image" && filter.networkRequestTypes.image) return true;
  if (type === "media" && filter.networkRequestTypes.media) return true;
  if (type === "manifest" && filter.networkRequestTypes.manifest) return true;
  if (type === "ws" && filter.networkRequestTypes.ws) return true;
  if (type === "wasm" && filter.networkRequestTypes.wasm) return true;
  return type === "other" && filter.networkRequestTypes.other;
};
